import { WSControlGroup } from "@wingspanhq/fe-component-library";
import { FundingSourceType } from "@wingspanhq/payments/dist/interfaces";
import { Currency } from "@wingspanhq/users/dist/lib/interfaces";
import {
  AccountHolderType,
  ExternalBankAccount,
  PayoutDestinationType,
  PreferredPayoutSpeed
} from "@wingspanhq/wingspan-api-sdk";
import { WSQueryCache } from "@ws-react-query";
import { useMemo, useState } from "react";
import { externalBankAccountsApi, payoutsApi } from "@api";
import { queriesExternalBankAccounts, queriesPayouts } from "@query";
import { selectorExternalBankAccountInstitution } from "@selectors/selectorExternalBankAccountInstitution";
import { selectorExternalBankAccountName } from "@selectors/selectorExternalBankAccountName";
import { selectorOwnPayoutSettings } from "@selectors/selectorOwnPayoutSettings";
import { useWSMutation } from "../../../query/helpers";
import { useUserId } from "../../../query/hooks/helpers";
import { QUERY_PAYROLL_SETTINGS } from "../../../query/payments/keys";
import { QUERY_USERS_CLIENT } from "../../../query/users/keys";
import { paymentsService } from "../../../services/payments";
import { usersService } from "../../../services/users";
import { StepLayoutWithOptions } from "../../../shared/components/Layout/StepLayoutWithOptions";
import { avatarBankAccount } from "../../../shared/constants/avatars";
import {
  concurrentActions,
  WSServiceError
} from "../../../utils/serviceHelper";

type Props = {
  title: string;
  description?: string;
  accounts: ExternalBankAccount[];
  onBack?: () => void;
  onNext?: () => void;
  setFundingSource?: boolean;
  setPaymentMethod?: boolean;
  setStandardPayoutDestination?: boolean;
  setInstantPayoutDestination?: boolean;
};

type HolderType = "personal" | "business";

async function updateAccountHolderTypes(
  accounts: ExternalBankAccount[],
  values: Record<string, HolderType>
) {
  return concurrentActions(
    accounts
      .filter(account => !account.accountHolderType)
      .map(account => async () => {
        try {
          const type = values[account.id];
          await externalBankAccountsApi.updateExternalBankAccount({
            bankAccountId: account.id,
            externalBankAccountUpdate: {
              accountHolderType:
                type === "business"
                  ? AccountHolderType.BUSINESS
                  : AccountHolderType.INDIVIDUAL
            }
          });
        } catch (error) {
          console.error("Failed to update account holder type", error);
        }
      })
  );
}

export const FormSelectAccount: React.FC<Props> = ({
  title,
  description,
  accounts,
  onBack,
  onNext,
  setFundingSource,
  setPaymentMethod,
  setStandardPayoutDestination,
  setInstantPayoutDestination
}) => {
  const userId = useUserId();

  const hasAccountsWithoutHolderType = useMemo(
    () => accounts.some(account => !account.accountHolderType),
    [accounts]
  );

  const [holderTypeValues, setHolderTypeValues] = useState(
    accounts.reduce<Record<string, HolderType>>((acc, account) => {
      if (account.accountHolderType) {
        return acc;
      }

      return {
        ...acc,
        [account.id]: "personal"
      };
    }, {})
  );

  const [submit, meta] = useWSMutation<
    void,
    WSServiceError,
    { accountId: string }
  >(
    async ({ accountId }) => {
      // It's fine to run it in the background
      updateAccountHolderTypes(accounts, holderTypeValues);

      if (setFundingSource) {
        const payrollSettings = await paymentsService.payrollSettings.update(
          userId,
          {
            fundingSource: {
              fundingSourceCurrency: Currency.USD,
              fundingSourceId: accountId,
              fundingSourceType: FundingSourceType.Account
            }
          }
        );

        WSQueryCache.setQueryData(QUERY_PAYROLL_SETTINGS, payrollSettings);
      }

      if (setPaymentMethod) {
        const client = await usersService.client.update(userId, {
          clientId: userId,
          profile: {
            defaultPaymentMethod: {
              accountId,
              paymentMethodId: null
            }
          }
        });

        WSQueryCache.setQueryData(QUERY_USERS_CLIENT, client);
      }

      if (setStandardPayoutDestination || setInstantPayoutDestination) {
        const payoutSettingsList =
          await queriesPayouts.listPayoutSettings.fetchQueryData();
        const payoutSettingsId =
          selectorOwnPayoutSettings(payoutSettingsList)?.id;

        if (payoutSettingsId && setStandardPayoutDestination) {
          await payoutsApi.updatePayoutSettings({
            payoutSettingsId,
            payoutSettingsUpdate: {
              preferredPayoutSpeed: PreferredPayoutSpeed.STANDARD,
              standardDestination: {
                id: accountId,
                type: PayoutDestinationType.EXTERNAL_BANK_ACCOUNT
              }
            }
          });
        }

        if (payoutSettingsId && setInstantPayoutDestination) {
          await payoutsApi.updatePayoutSettings({
            payoutSettingsId,
            payoutSettingsUpdate: {
              preferredPayoutSpeed: PreferredPayoutSpeed.INSTANT,
              instantDestination: {
                id: accountId,
                type: PayoutDestinationType.EXTERNAL_BANK_ACCOUNT
              }
            }
          });
        }
      }
    },
    {
      onSuccess: onNext,
      dependencies: [
        QUERY_PAYROLL_SETTINGS,
        queriesPayouts.listPayoutSettings.queryKeyBase,
        queriesPayouts.getPayoutSettings.queryKeyBase,
        queriesExternalBankAccounts.listExternalBankAccounts.queryKeyBase
      ]
    }
  );

  return (
    <StepLayoutWithOptions
      key="accounts"
      title={title}
      description={description}
      options={accounts.map(account => ({
        header: {
          label: {
            text:
              selectorExternalBankAccountInstitution(account) ||
              account.accountName,
            secondaryText: selectorExternalBankAccountName(account),
            avatar: avatarBankAccount
          }
        },
        value: account.id,
        children: account.accountHolderType ? undefined : (
          <WSControlGroup
            label="Account type"
            helperText="Specify whether this is a personal or business bank account"
            type="radio"
            name={`account-type-${account.id}`}
            value={holderTypeValues[account.id]}
            onChange={newValue => {
              setHolderTypeValues(prev => ({
                ...prev,
                [account.id]: newValue as HolderType
              }));
            }}
            options={[
              {
                value: "personal",
                label: "Personal"
              },
              {
                value: "business",
                label: "Business"
              }
            ]}
          />
        )
      }))}
      onBack={onBack}
      onNext={accountId => {
        submit({ accountId });
      }}
      error={meta.error}
      isLoading={meta.isLoading}
      alerts={
        hasAccountsWithoutHolderType
          ? [
              {
                theme: "info",
                icon: "info-circle",
                title: "Additional info required",
                children:
                  "Some accounts need additional information. Specify whether these are personal or business bank accounts."
              }
            ]
          : []
      }
    />
  );
};
